// src/pdfViewer.js
//
// pdf.js lifecycle for the Manuals viewer, deliberately free of Vue.
//
// The whole reason this module exists, recorded here because it is the only
// place a maintainer will look before "simplifying" it:
//
// vue-pdf installs ONE worker at import time (vuePdfNoSss.vue:14 sets
// GlobalWorkerOptions.workerPort once and never replaces or terminates it) and
// serialises every page load and render through a single module-global promise
// (pdfjsWrapper.js:3), which it replaces wholesale with an unguarded
// pdfDoc.destroy() on any page unmount (:71). A viewer that opens and closes
// many documents violates both assumptions, and that mismatch produced, in
// order: the "second manual won't load" bug (INFS-1086), an 892 KB worker leak
// per manual opened, and a fast-switch wedge — correct page count, blank
// canvases, no error, permanent until reload — that survived four separately
// merged production fixes before this rewrite (INFS-1352).
const PDFJS = require("pdfjs-dist/es5/build/pdf.js");

// We set workerSrc and NEVER workerPort. That makes pdf.js construct the
// worker itself, which sets PDFWorker._webWorker, which means destroy()
// terminates the thread for us — no manual terminate, no leak, no timing
// window to get wrong.
//
// The `!!` prefix makes file-loader the ONLY loader applied to this request.
// Without it, the request is composed with vue-cli's configured rules.
// worker-loader escapes that by short-circuiting in its pitch phase;
// file-loader 4.3.0 exports no pitch, so it would be composed instead.
// (vue-cli's babel rule does exclude node_modules, so babel specifically is
// not the hazard — any configured rule matching .js is.)
// Point at pdf.worker.MIN.js. file-loader copies the file verbatim with no
// terser pass, so the unminified build ships 1.76 MiB where the minified one
// is 0.90 MiB — and worker-loader's old output was 0.85 MiB. Using the
// unminified file would roughly double the worker download for every user.
const workerSrcModule = require("!!file-loader?name=js/pdf.worker.[contenthash:8].js!pdfjs-dist/es5/build/pdf.worker.min.js");
PDFJS.GlobalWorkerOptions.workerSrc = workerSrcModule.default || workerSrcModule;

// Above this many pages, skip the per-page viewport fetch on open and reuse
// page 1's size for every placeholder. Trades exact initial scroll height for
// a bounded open time on very large documents.
const EXACT_SIZE_PAGE_LIMIT = 200;

const { PDFLinkService, EventBus } = require("pdfjs-dist/es5/web/pdf_viewer");

export function createPdfViewer({ onError, onNavigate } = {}) {
  let doc = null;
  // LinkTarget and DEFAULT_LINK_REL live on the BUILD, not on pdf_viewer,
  // which exports neither and would hand us undefined silently.
  // An eventBus is required, not optional: executeNamedAction ends with an
  // unguarded `this.eventBus.dispatch(...)` (pdf_viewer.js:2208). Without one,
  // any /S /Named link throws inside its own onclick, so pdf.js's `return
  // false` never runs, the browser follows the anchor's empty href, and the
  // fragment this hash-routed app depends on is cleared — bouncing the user
  // off the page entirely.
  const eventBus = new EventBus();
  const linkService = new PDFLinkService({
    eventBus,
    externalLinkTarget: PDFJS.LinkTarget.BLANK,
    // Passed as a literal on purpose. `LinkTarget` IS re-exported on the build
    // (pdf.js:141-144) but DEFAULT_LINK_REL is NOT — reading
    // PDFJS.DEFAULT_LINK_REL yields undefined, which pdf.js then silently
    // replaces with this same default (pdf.js:954). Relying on that fallback
    // works but hides the security-relevant value; state it.
    externalLinkRel: "noopener noreferrer nofollow",
  });

  // Most links in these manuals are INTERNAL destinations (table-of-contents
  // entries), not external URLs. pdf.js binds `link.onclick` to
  // `linkService.navigateTo(dest)` (pdf.js:21043-21050), which resolves the
  // destination through `this.pdfDocument` (getDestination / getPageIndex) and
  // then asks `this.pdfViewer` to scroll. Both start null
  // (pdf_viewer.js:1948-1949), so without them a click throws "Cannot read
  // properties of null (reading 'getPageIndex')" and nothing moves — verified
  // in a browser, and the reason vue-pdf called setDocument and setViewer.
  // pdfHistory is left null deliberately: navigateTo guards it
  // (pdf_viewer.js:2012).
  let currentPageNumber = 1;
  linkService.setViewer({
    // destArray and ignoreDestinationZoom (pdf_viewer.js:2022-2026) are
    // deliberately ignored: this viewer has no zoom and scrolls to the top of
    // the target page, which is the right behaviour for a table of contents.
    scrollPageIntoView: ({ pageNumber }) => {
      currentPageNumber = pageNumber || 1;
      if (onNavigate) onNavigate(currentPageNumber);
    },
    get currentPageNumber() {
      return currentPageNumber;
    },
    set currentPageNumber(value) {
      // Reached by NextPage/PrevPage/FirstPage/LastPage named actions, which
      // go through PDFLinkService's `page` setter (pdf_viewer.js:2240-2246).
      // Mutating the local alone would move nothing on screen.
      currentPageNumber = value || 1;
      if (onNavigate) onNavigate(currentPageNumber);
    },
  });
  // The loading task is reachable while a document is still loading. This is
  // the only handle to the Web Worker during that window: getDocument() spawns
  // the thread synchronously (pdf.js:12396-12397 sets task._worker before
  // _fetchDocument), and task.destroy() (pdf.js:12508-12523) is the only
  // public route to it. Without this, destroy() reports success while a worker
  // keeps parsing and downloading — one leak per open/close cycle, and
  // permanent if the connection stalls, since pdf.js has no load timeout.
  let loadingTask = null;
  let destroyed = false;
  let gen = 0;
  const mounted = new Map(); // page number -> { task, el }

  const report = (err) => {
    if (onError) onError(err);
    else console.error("pdfViewer:", err);
  };

  // The `type` clause is what actually works. `err.name` comes from
  // this.constructor.name (pdf.js:2217-2225) and vue-cli's terser mangles it, so
  // the name comparison is dead in a production build; `type` is an explicit
  // property pdf.js sets to "canvas" (pdf.js:14686). Keep both — the name check
  // is still useful in a dev build — but do not rely on it alone.
  const isCancelled = (err) =>
    !!err && (err.name === "RenderingCancelledException" || err.type === "canvas");

  function clearEntry(entry) {
    if (entry.task) {
      try {
        entry.task.cancel();
      } catch (e) {
        // Defensive only: cancel() on a settled task is a no-op in
        // pdfjs-dist 2.6.347 (pdf.js:14671-14685 sets flags, guards
        // gfx.endDrawing(), and re-rejects an already-settled capability).
      }
      entry.task = null;
    }
    if (entry.el) entry.el.innerHTML = "";
  }

  async function teardown() {
    mounted.forEach(clearEntry);
    mounted.clear();
    // At most one of these is ever set: open() clears loadingTask at the moment
    // it adopts doc, so a mid-load teardown reclaims the task and a
    // post-adoption teardown reclaims the document. There is a brief window in
    // open()'s success path where neither is set; it contains no await, so no
    // caller can observe it.
    const oldTask = loadingTask;
    const old = doc;
    loadingTask = null;
    doc = null;
    linkService.setDocument(null);
    if (oldTask) {
      try {
        // Aborts the in-flight load and terminates its worker.
        await oldTask.destroy();
      } catch (e) {
        // Teardown rejections are swallowed; there is no shared queue to wedge.
      }
    }
    if (!old) return;
    try {
      // Terminates the worker, because pdf.js created it.
      await old.destroy();
    } catch (e) {
      // A failed teardown can no longer block anything: there is no shared
      // queue for it to wedge.
    }
  }

  return {
    async open(url) {
      // Terminal after destroy(): a watcher or timer firing post-teardown must
      // not spawn a document and worker that nothing will ever reclaim.
      if (destroyed) return null;
      const g = ++gen;
      await teardown();
      if (g !== gen) return null;

      // Retain the task, not just its promise. getDocument() spawns the Web
      // Worker SYNCHRONOUSLY — pdf.js:12396-12397 does `new PDFWorker(...)`
      // and sets `task._worker` before _fetchDocument() is even called — and
      // task.destroy() (pdf.js:12517-12520) is the only route to that thread.
      // Discarding the task means any rejection (404, CORS, corrupt file, or a
      // superseded call that also fails) leaks a worker permanently, which is
      // the exact bug class this module exists to eliminate.
      //
      // Defaults only on getDocument — see the CORS section of the spec.
      const task = PDFJS.getDocument(url);
      loadingTask = task;
      let loaded;
      try {
        loaded = await task.promise;
      } catch (err) {
        if (loadingTask === task) loadingTask = null;
        try {
          await task.destroy();
        } catch (e) {
          /* nothing left to salvage; the point is to reclaim the worker */
        }
        if (g !== gen) return null;
        throw err;
      }
      if (loadingTask === task) loadingTask = null;
      if (g !== gen) {
        try {
          await loaded.destroy();
        } catch (e) {
          /* superseded before we adopted it */
        }
        return null;
      }
      doc = loaded;
      // Internal link destinations resolve through the document. The second
      // argument is baseUrl: without it, getAnchorUrl("") returns "" and an
      // internal link's href is a bare `#…` fragment, so a ctrl/middle-click
      // (which bypasses onclick entirely) navigates this hash-routed app to a
      // route it cannot match. With it, the href is the PDF's own URL plus the
      // fragment.
      linkService.setDocument(loaded, url);

      const numPages = doc.numPages;
      const pageSizes = [];
      try {
        // Read `loaded`, not the mutable `doc`: a concurrent teardown nulls
        // `doc` and would turn this into a TypeError laundered into a null
        // return, hiding a real pdf.js failure.
        const firstViewport = (await loaded.getPage(1)).getViewport({ scale: 1 });
        if (g !== gen) return null;
        pageSizes.push({ w: firstViewport.width, h: firstViewport.height });

        if (numPages <= EXACT_SIZE_PAGE_LIMIT) {
          for (let n = 2; n <= numPages; n++) {
            const viewport = (await loaded.getPage(n)).getViewport({ scale: 1 });
            if (g !== gen) return null;
            pageSizes.push({ w: viewport.width, h: viewport.height });
          }
        } else {
          for (let n = 2; n <= numPages; n++) {
            pageSizes.push({ w: firstViewport.width, h: firstViewport.height });
          }
        }
      } catch (err) {
        // pdfjs-dist 2.6.347: WorkerTransport.destroy() sets destroyed = true
        // synchronously, and getPage() then throws Error("Transport destroyed")
        // when its response lands. So a concurrent open()/destroy() makes an
        // in-flight getPage REJECT rather than hang. That is supersession, and
        // this method's contract is to return null for it, never to reject.
        if (g !== gen) return null;
        // A genuine failure while sizing pages: the caller will treat open() as
        // failed, so do not leave an adopted document — and its worker — alive
        // with no handle for the caller to reach. teardown() nulls and destroys
        // it, swallowing its own errors. Bump the generation first so
        // "teardown implies the generation changed" stays true globally, which
        // is the invariant isStale() and the comments above rely on.
        gen++;
        await teardown();
        throw err;
      }

      return { numPages, pageSizes };
    },

    async mountPage(n, el) {
      if (!doc || !el || mounted.has(n)) return;
      const g = gen;
      const entry = { task: null, el };
      mounted.set(n, entry);
      // Superseded either by a newer document (generation) or by this page
      // being unmounted and remounted (entry identity).
      const isStale = () => g !== gen || mounted.get(n) !== entry;

      try {
        const page = await doc.getPage(n);
        if (isStale()) return;

        const cssWidth = el.clientWidth;
        if (!cssWidth) {
          // Placeholder not laid out yet — realistic mid Bootstrap modal
          // transition. Drop the entry so a later mount renders at the real
          // width, rather than recording a zero-width canvas as a success that
          // mounted.has(n) would then block forever. clientWidth is an integer,
          // so the falsy case is exactly 0.
          if (mounted.get(n) === entry) mounted.delete(n);
          return;
        }
        const base = page.getViewport({ scale: 1 });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: (cssWidth / base.width) * dpr });

        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = "100%";
        canvas.style.display = "block";
        el.appendChild(canvas);

        entry.task = page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
        });
        await entry.task.promise;
        if (isStale()) return;
        entry.task = null;

        // Annotation failures must not discard a canvas that rendered fine, so
        // this has its own try/catch rather than sharing the canvas one.
        try {
          const annotations = await page.getAnnotations({ intent: "display" });
          // isStale() is mountPage's existing supersession predicate; do not
          // re-inline the generation/identity comparison here.
          if (isStale()) return;
          if (!annotations.length) return;

          const layer = document.createElement("div");
          layer.className = "annotationLayer";
          el.appendChild(layer);
          // Build the layer from a CSS-pixel viewport, NOT the device-scaled
          // one used for the canvas. pdf.js positions each annotation with
          // `container.style.transform = matrix(viewport.transform)`
          // (pdf.js:20915), so the viewport's scale is baked into the
          // transform. The canvas may be DPR-scaled because its backing store
          // is sized in device pixels and `style.width: 100%` scales it back
          // down visually; the annotation layer is plain DOM with no such
          // downscale, so reusing the DPR-scaled viewport would place every
          // link at dpr x its correct offset — mostly off the page on a
          // retina screen.
          const cssViewport = page.getViewport({
            scale: cssWidth / base.width,
            dontFlip: true,
          });
          PDFJS.AnnotationLayer.render({
            viewport: cssViewport,
            div: layer,
            annotations,
            page,
            linkService,
            renderInteractiveForms: false,
          });
          // Retained so refreshAnnotations() can re-apply the transforms
          // after a width change; see the note there.
          entry.layer = layer;
          entry.annotations = annotations;
          entry.page = page;
          entry.base = base;
        } catch (annotErr) {
          // The page's pixels are already on screen; a broken annotation
          // dict must not blank it.
          report(annotErr);
        }
      } catch (err) {
        // Teardown arrives here as a rejection too — see the note in open().
        // Detect it by generation and entry identity rather than by matching
        // an error message: cheaper, and exhaustive over every teardown path.
        const superseded = isStale();
        if (mounted.get(n) === entry) {
          // Clear the canvas we may already have appended, so a later remount
          // of this page cannot stack a second one in the same placeholder.
          clearEntry(entry);
          mounted.delete(n);
        }
        if (superseded || isCancelled(err)) return;
        // One page failing must not blank the rest of the document.
        report(err);
      }
    },

    // Re-apply annotation transforms after a width change. The placeholders are
    // keyed by page number, so a resize REUSES the same elements: the canvas
    // rescales itself through style.width:100%, but each annotation's geometry
    // was baked in CSS pixels at mount time
    // (container.style.transform = matrix(viewport.transform), pdf.js:20915).
    // Without this the page looks correct while every clickable region is
    // silently offset, and stays wrong until that page scrolls out of the lazy
    // window and back. AnnotationLayer.update re-applies only the matrix
    // (pdf.js:22185-22206), so it is cheap.
    refreshAnnotations() {
      mounted.forEach((entry) => {
        if (!entry.layer || !entry.annotations || !entry.page || !entry.base) {
          return;
        }
        const cssWidth = entry.el && entry.el.clientWidth;
        if (!cssWidth) return;
        PDFJS.AnnotationLayer.update({
          viewport: entry.page.getViewport({
            scale: cssWidth / entry.base.width,
            dontFlip: true,
          }),
          div: entry.layer,
          annotations: entry.annotations,
        });
      });
    },

    unmountPage(n) {
      const entry = mounted.get(n);
      if (!entry) return;
      clearEntry(entry);
      mounted.delete(n);
    },

    async destroy() {
      destroyed = true;
      gen++;
      await teardown();
    },
  };
}
